// Design types
export interface Design {
  id: string;
  userId: string | null;
  createdAt: number;
  updatedAt: number;
  text: string;
  style: DesignStyle;
  uchiwaType: UchiwaType;
  isPublic: boolean;
  aiGenerated: boolean;
  thumbnailDataUrl?: string;
}

export interface DesignStyle {
  preset: StylePreset;
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
  secondaryColor: string;
  fontSize: number;
  textOrientation: 'horizontal' | 'vertical';
  effects: StyleEffect[];
  aiPrompt?: string;
  aiVariantIndex?: number;
}

export type StyleEffect =
  | { type: 'shadow'; offsetX: number; offsetY: number; blur: number; color: string }
  | { type: 'outline'; width: number; color: string }
  | { type: 'gradient'; stops: Array<{ offset: number; color: string }> }
  | { type: 'glow'; radius: number; color: string; opacity: number };

export type StylePreset =
  | 'purupuru'
  | 'glitter'
  | 'simple'
  | 'bold'
  | 'gyaru'
  | 'shadow3d'
  | 'neon'
  | 'cute'
  | 'ai-custom';

export type UchiwaType = 'jumbo' | 'regular' | 'mini';

export interface StoredDesign extends Design {
  _schemaVersion: number;
}

// Renderer types
export interface RendererContext {
  mode: 'preview' | 'pdf';
  dpi: number;
  scaleFactor: number;
}

export interface RenderParams {
  text: string;
  textColor: string;
  backgroundColor: string;
  secondaryColor: string;
  fontSize: number;
  textOrientation: 'horizontal' | 'vertical';
  canvasWidth: number;
  canvasHeight: number;
  effects: StyleEffect[];
}

export interface StyleRenderer {
  name: StylePreset;
  label: string;
  description: string;
  render(ctx: CanvasRenderingContext2D, params: RenderParams, rc: RendererContext): void;
  renderThumbnail(ctx: CanvasRenderingContext2D, size: number): void;
}

// Uchiwa sizes (mm)
export const UCHIWA_SIZES = {
  jumbo: {
    width: 295,
    height: 285,
    printableArea: { width: 275, height: 265 },
    cornerRadius: 60,
  },
  regular: {
    width: 220,
    height: 210,
    printableArea: { width: 200, height: 190 },
    cornerRadius: 45,
  },
} as const;

// User types
export interface User {
  id: string;
  email: string;
  googleId: string;
  displayName: string;
  createdAt: number;
  plan: 'free' | 'pro';
  planExpiresAt?: number;
  aiGenerationsThisMonth: number;
  pdfExportsThisMonth: number;
  lastResetAt: number;
  totalSinglePurchases: number;
}
